import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Paper, Typography, Box, Modal, Button, IconButton, Chip, TextField,
    Select, MenuItem, Tooltip, Pagination
} from '@mui/material';
import { Icon } from '@iconify/react';
import { getData, postData } from '../../../webService/webservice.js';
import { toast } from 'react-toastify';
import PackageInvoiceModal from '../../../components/Subscription/PackageInvoiceModal.jsx';
import '../../../styles/Auth/package-management.css';

const calculateTotalValidityClient = (baseValidity, validityUnit, extraValidity = 0, extraValidityUnit = 'Days') => {
    const baseNum = Number(baseValidity) || 0;
    const extraNum = Number(extraValidity) || 0;
    if (baseNum <= 0) return "-";
    const baseUnitSingular = validityUnit ? validityUnit.replace(/s$/i, '') : 'Month';
    const baseUnitStr = baseNum === 1 ? baseUnitSingular : `${baseUnitSingular}s`;
    const baseStr = `${baseNum} ${baseUnitStr}`;

    if (extraNum <= 0) return baseStr;

    const extraUnitSingular = extraValidityUnit ? extraValidityUnit.replace(/s$/i, '') : 'Day';
    if (validityUnit === extraValidityUnit) {
        const total = baseNum + extraNum;
        return `${total} ${total === 1 ? baseUnitSingular : `${baseUnitSingular}s`}`;
    }
    if (validityUnit === 'Years' && extraValidityUnit === 'Months') {
        const totalMonths = (baseNum * 12) + extraNum;
        return `${totalMonths} Months`;
    }
    const extraUnitStr = extraNum === 1 ? extraUnitSingular : `${extraUnitSingular}s`;
    return `${baseStr} ${extraNum} ${extraUnitStr}`;
};

const SubscriptionHistoryPage = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const role = localStorage.getItem("role");

    // Pagination & Filters
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Stats
    const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, renewed: 0 });

    // Grace Modal
    const [isGraceModalOpen, setIsGraceModalOpen] = useState(false);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
    const [graceDays, setGraceDays] = useState(7);
    const [extending, setExtending] = useState(false);

    // View Modal
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewData, setViewData] = useState(null);

    // Invoice Modal
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [selectedInvoiceSubId, setSelectedInvoiceSubId] = useState(null);

    const handleOpenInvoiceModal = (sub) => {
        const subId = sub._id || sub.id;
        setSelectedInvoiceSubId(subId);
        setIsInvoiceModalOpen(true);
    };

    // Store History Modal
    const [isStoreHistoryModalOpen, setIsStoreHistoryModalOpen] = useState(false);
    const [selectedStoreForHistory, setSelectedStoreForHistory] = useState(null);
    const [storeHistoryList, setStoreHistoryList] = useState([]);
    const [storeHistoryLoading, setStoreHistoryLoading] = useState(false);

    const isSubscriptionExpired = (sub) => {
        if (!sub) return false;
        if (sub.subscriptionStatus === 'Expired') return true;
        if (sub.finalExpiryDate) {
            const options = { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' };
            const nowIST = new Date().toLocaleDateString('en-CA', options);
            const expiryIST = new Date(sub.finalExpiryDate).toLocaleDateString('en-CA', options);
            if (expiryIST < nowIST) return true;
        }
        if (sub.remainingDays !== undefined && sub.remainingDays !== null && sub.remainingDays <= 0) {
            return true;
        }
        return false;
    };

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("Token");
            const res = await getData('store-subscriptions', { page, limit, search: searchQuery, status: statusFilter }, token);
            if (res?.status === 200 || res?.success) {
                setSubscriptions(res.data?.subscriptions || []);
                setTotalPages(res.data?.totalPages || 1);
                setTotalCount(res.data?.totalCount || 0);
            } else {
                toast.error("Failed to load store subscriptions");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchQuery, statusFilter]);

    const fetchStats = useCallback(async () => {
        try {
            const token = localStorage.getItem("Token");
            const [allRes, activeRes, expiredRes, renewedRes] = await Promise.all([
                getData("store-subscriptions", { limit: 1 }, token),
                getData("store-subscriptions", { limit: 1, status: "Active" }, token),
                getData("store-subscriptions", { limit: 1, status: "Expired" }, token),
                getData("store-subscriptions", { limit: 1, status: "Renewed" }, token)
            ]);
            setStats({
                total: allRes?.data?.totalCount || 0,
                active: activeRes?.data?.totalCount || 0,
                expired: expiredRes?.data?.totalCount || 0,
                renewed: renewedRes?.data?.totalCount || 0
            });
        } catch (err) {
            console.error("Error fetching stats:", err);
        }
    }, []);

    const [storesMap, setStoresMap] = useState({});

    const fetchStoresMap = useCallback(async () => {
        try {
            const token = localStorage.getItem("Token");
            const res = await getData('stores/stores', { limit: 100000000 }, token);
            const storesList = res?.data?.results || (Array.isArray(res?.data) ? res?.data : []);
            const map = {};
            storesList.forEach(s => {
                if (s._id) {
                    map[s._id] = s.storeName || s.branchName;
                }
            });
            setStoresMap(map);
        } catch (err) {
            console.error("Error fetching stores map:", err);
        }
    }, []);

    const getStoreName = useCallback((sub) => {
        if (!sub) return "Unknown";
        const sId = typeof sub.storeId === 'object' ? sub.storeId?._id : sub.storeId;
        if (sId && storesMap[sId]) {
            return storesMap[sId];
        }
        const fromStoreObj = sub.storeId?.storeName || sub.storeId?.branchName;
        if (fromStoreObj) return fromStoreObj;

        const storeDetails = Array.isArray(sub.storeDetails) ? sub.storeDetails[0] : sub.storeDetails;
        const fromDetails = storeDetails?.storeName || storeDetails?.branchName;
        if (fromDetails) return fromDetails;

        return sub.storeName || "Unknown";
    }, [storesMap]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchStoresMap();
    }, [fetchStoresMap]);

    // Extend Grace Actions
    const handleOpenGraceModal = (subscriptionId) => {
        setSelectedSubscriptionId(subscriptionId);
        setGraceDays(7);
        setIsGraceModalOpen(true);
    };

    const handleExtendGracePeriod = async () => {
        if (graceDays <= 0) {
            toast.error("Grace period must be at least 1 day");
            return;
        }
        setExtending(true);
        try {
            const token = localStorage.getItem("Token");
            const res = await postData('store-subscriptions/extend-grace', {
                subscriptionId: selectedSubscriptionId,
                graceDays
            }, token);

            if (res?.success || res?.status === 200) {
                toast.success(res.message || "Grace period extended successfully!");
                setIsGraceModalOpen(false);
                fetchHistory();
                fetchStats();
            } else {
                toast.error(res.message || "Failed to extend grace period");
            }
        } catch (error) {
            toast.error(error.message || "An error occurred while extending grace period");
        } finally {
            setExtending(false);
        }
    };

    // View Details Actions
    const handleOpenViewModal = (sub) => {
        setViewData(sub);
        setIsViewModalOpen(true);
    };

    const handleOpenStoreHistoryModal = async (sub) => {
        const storeId = typeof sub.storeId === 'object' ? sub.storeId?._id : sub.storeId;
        const storeName = getStoreName(sub);
        setSelectedStoreForHistory({ storeId, storeName });
        setIsStoreHistoryModalOpen(true);
        setStoreHistoryLoading(true);
        try {
            const token = localStorage.getItem("Token");
            let res = await getData(`store-subscriptions/store-history/${storeId}`, {}, token);
            if (!res || (!res.data && !res.success)) {
                res = await getData(`store-subscriptions/history/${storeId}`, {}, token);
            }
            if (res?.status === 200 || res?.success) {
                const list = res.data?.subscriptions || (Array.isArray(res.data) ? res.data : []);
                setStoreHistoryList(list);
            } else {
                toast.error("Failed to load subscription history for this store");
                setStoreHistoryList([]);
            }
        } catch (err) {
            console.error("Error fetching store subscription history:", err);
            toast.error("Error loading subscription history");
            setStoreHistoryList([]);
        } finally {
            setStoreHistoryLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatCurrency = (amt) => {
        const num = Number(amt || 0);
        if (Number.isInteger(num)) {
            return `₹${num.toLocaleString('en-IN')}`;
        }
        return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const getStatusChip = (status) => {
        if (status === 'Active') return <Chip label="ACTIVE" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: '11px' }} />;
        if (status === 'Expired') return <Chip label="EXPIRED" size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '11px' }} />;
        if (status === 'Renewed') return <Chip label="RENEWED" size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 700, fontSize: '11px' }} />;
        return <Chip label={status || 'UNKNOWN'} size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: '11px' }} />;
    };

    return (
        <div className="product-layout">
            <div className="package-management-container" style={{ minHeight: 'auto', padding: '24px 20px 20px' }}>
                <div className="package-header">
                    <div className="package-header-title">
                        <div className="package-header-icon">
                            <Icon icon="mdi:store-check-outline" width="24" height="24" />
                        </div>
                        <div>
                            <h2>Store Subscriptions</h2>
                            <p>View and manage store subscription history and validity.</p>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="package-stats-grid">
                    <div className="package-stat-card">
                        <div className="stat-icon-wrapper all">
                            <Icon icon="mdi:package-variant" />
                        </div>
                        <div className="stat-info">
                            <h4>{stats.total}</h4>
                            <span>Total Subscriptions</span>
                        </div>
                    </div>
                    <div className="package-stat-card">
                        <div className="stat-icon-wrapper active">
                            <Icon icon="mdi:check-circle-outline" />
                        </div>
                        <div className="stat-info">
                            <h4>{stats.active}</h4>
                            <span>Active Subscriptions</span>
                        </div>
                    </div>
                    <div className="package-stat-card">
                        <div className="stat-icon-wrapper inactive">
                            <Icon icon="mdi:close-circle-outline" style={{ color: '#ef4444' }} />
                        </div>
                        <div className="stat-info">
                            <h4>{stats.expired}</h4>
                            <span>Expired Subscriptions</span>
                        </div>
                    </div>
                    <div className="package-stat-card">
                        <div className="stat-icon-wrapper" style={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}>
                            <Icon icon="mdi:autorenew" />
                        </div>
                        <div className="stat-info">
                            <h4>{stats.renewed}</h4>
                            <span>Renewed Subscriptions</span>
                        </div>
                    </div>
                </div>

                {/* Controls Card */}
                <div className="package-controls-card">
                    <div className="search-box-wrapper">
                        <Icon icon="mdi:magnify" width="20" height="20" color="#64748b" />
                        <input
                            type="text"
                            placeholder="Search by store or package name..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setPage(1);
                            }}
                        />
                        {searchQuery && (
                            <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(1); }}>
                                <Icon icon="mdi:close" width="16" height="16" />
                            </IconButton>
                        )}
                    </div>

                    <div className="filter-group">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                            Status Filter:
                        </Typography>
                        <Select
                            size="small"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            sx={{
                                minWidth: 140,
                                borderRadius: '8px',
                                bgcolor: '#ffffff',
                                '& .MuiSelect-select': { py: 1, fontSize: '13.5px', fontWeight: 600 }
                            }}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Expired">Expired</MenuItem>
                            <MenuItem value="Renewed">Renewed</MenuItem>
                        </Select>
                    </div>
                </div>

                {/* Table */}
                <TableContainer component={Paper} className="package-table-container">
                    <Table className="package-table">
                        <TableHead sx={{ background: '#3C0008 !important' }}>
                            <TableRow sx={{ background: '#3C0008 !important' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Store</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Package</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Price</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>GST Details</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>GST Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Total Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Start Date</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Validity</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Expiry Date</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Status</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={11} align="center">Loading...</TableCell></TableRow>
                            ) : subscriptions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ textAlign: 'center', color: '#94a3b8' }}>
                                            <Icon icon="mdi:store-off-outline" width="48" height="48" style={{ opacity: 0.5, marginBottom: '8px' }} />
                                            <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600, fontSize: '16px' }}>
                                                No Subscriptions Found
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                subscriptions.map(sub => {
                                    const storeName = getStoreName(sub);
                                    return (
                                        <TableRow key={sub._id}>
                                            <TableCell>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                                    {storeName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {sub.packageName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#3C0008' }}>
                                                    {formatCurrency(sub.packagePrice)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {sub.isGstApplicable ? (
                                                    <span className={`gst-badge ${sub.gstType === 'Inclusive' ? 'inclusive' : 'exclusive'}`}>
                                                        {sub.gstPercentage}% ({sub.gstType})
                                                    </span>
                                                ) : (
                                                    <span className="gst-badge">No GST</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                                                    {formatCurrency(sub.gstAmount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 800, color: '#166534' }}>
                                                    ₹{Math.round(sub.totalAmount || 0).toLocaleString('en-IN')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{formatDate(sub.validityStartDate || sub.subscriptionStartDate)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb' }}>
                                                    {sub.totalValidity || calculateTotalValidityClient(sub.baseValidity, sub.validityUnit, sub.extraValidity, sub.extraValidityUnit)}
                                                </Typography>
                                                {(sub.extraValidity > 0) && (
                                                    <Typography variant="caption" sx={{ color: '#b45309', display: 'block' }}>
                                                        (+{sub.extraValidity} {sub.extraValidityUnit} offer)
                                                    </Typography>
                                                )}
                                                {(sub.totalGraceDays > 0) && (
                                                    <Typography variant="caption" sx={{ color: '#dc2626', display: 'block' }}>
                                                        (+{sub.totalGraceDays} Days grace)
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatDate(sub.finalExpiryDate)}</TableCell>
                                            <TableCell>
                                                {getStatusChip(sub.subscriptionStatus)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <div className="action-btn-group" style={{ justifyContent: 'center' }}>
                                                    <Tooltip title="View Current Subscription Details">
                                                        <IconButton size="small" className="action-icon-btn view" onClick={() => handleOpenViewModal(sub)}>
                                                            <Icon icon="mdi:eye-outline" width="18" height="18" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Print Package Invoice">
                                                        <IconButton size="small" className="action-icon-btn view" onClick={() => handleOpenInvoiceModal(sub)} sx={{ color: '#3c0008', bgcolor: '#fff5f5' }}>
                                                            <Icon icon="mdi:printer" width="18" height="18" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Subscription History">
                                                        <IconButton size="small" className="action-icon-btn edit" onClick={() => handleOpenStoreHistoryModal(sub)} sx={{ color: '#2563eb' }}>
                                                            <Icon icon="mdi:history" width="18" height="18" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {["SuperAdmin", "SelfAdmin"].includes(role) && isSubscriptionExpired(sub) && (
                                                        <Tooltip title="Extend Grace Period">
                                                            <IconButton size="small" className="action-icon-btn toggle-active" onClick={() => handleOpenGraceModal(sub._id)}>
                                                                <Icon icon="mdi:calendar-clock" width="18" height="18" color="#ef4444" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, px: 1 }}>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Showing {subscriptions.length} of {totalCount} subscriptions
                        </Typography>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, p) => setPage(p)}
                            color="primary"
                            sx={{
                                '& .Mui-selected': { bgcolor: '#3C0008 !important', color: '#ffffff' }
                            }}
                        />
                    </Box>
                )}

                {/* View Modal */}
                <Modal open={isViewModalOpen} onClose={() => setIsViewModalOpen(false)}>
                    <Box className="package-modal-box" sx={{ maxWidth: '600px !important' }}>
                        <div className="modal-header-banner">
                            <h3>
                                <Icon icon="mdi:store-search-outline" width="22" height="22" />
                                Store Subscription Details
                            </h3>
                            <IconButton size="small" onClick={() => setIsViewModalOpen(false)} sx={{ color: '#ffffff' }}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>
                        {viewData && (
                            <div className="modal-body-scrollable" style={{ padding: '24px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                            {getStoreName(viewData)}
                                        </Typography>
                                        {/* <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            Store ID: {viewData.storeId?._id || viewData.storeId || "-"}
                                        </Typography> */}
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        {getStatusChip(viewData.subscriptionStatus)}
                                    </Box>
                                </Box>

                                <div className="form-grid" style={{ gap: '20px' }}>
                                    <div>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Package Details
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5, color: '#1e293b' }}>
                                            {viewData.packageName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mt: 0.5 }}>
                                            Price: {formatCurrency(viewData.packagePrice)}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Billing Details
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 800, mt: 0.5, color: '#166534' }}>
                                            Total: {formatCurrency(Math.round(viewData.totalAmount || 0))}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                            GST: {viewData.isGstApplicable ? `${viewData.gstPercentage}% ${viewData.gstType} (${formatCurrency(viewData.gstAmount)})` : "Not Applicable"}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Validity & Dates
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mt: 0.5 }}>
                                            Start Date: <span style={{ color: '#1e293b' }}>{formatDate(viewData.validityStartDate || viewData.subscriptionStartDate)}</span>
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mt: 0.5 }}>
                                            Base Validity: <span style={{ color: '#1e293b' }}>{viewData.baseValidity} {viewData.validityUnit}</span>
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mt: 0.5 }}>
                                            Extra Validity / Offer: <span style={{ color: '#1e293b' }}>{viewData.extraValidity > 0 ? `${viewData.extraValidity} ${viewData.extraValidityUnit}` : "None"}</span>
                                        </Typography>
                                        {viewData.totalGraceDays > 0 && (
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mt: 0.5 }}>
                                                Grace Period: <span style={{ color: '#b45309' }}>{viewData.totalGraceDays} Days</span>
                                            </Typography>
                                        )}
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mt: 0.5 }}>
                                            Final Expiry Date: <span style={{ color: '#dc2626' }}>{formatDate(viewData.finalExpiryDate)}</span>
                                        </Typography>
                                    </div>
                                </div>

                                {/* Grace Period Extension History Logs */}
                                {viewData.gracePeriodLogs && viewData.gracePeriodLogs.length > 0 && (
                                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                                            <Icon icon="mdi:calendar-clock" width="16" height="16" color="#b45309" />
                                            Grace Period Extension Logs ({viewData.gracePeriodLogs.length})
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden', borderColor: '#fde68a' }}>
                                            <Table size="small">
                                                <TableHead sx={{ background: '#3C0008 !important' }}>
                                                    <TableRow sx={{ background: '#3C0008 !important' }}>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', letterSpacing: '0.5px' }}>#</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Extension Days</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Previous Expiry</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', letterSpacing: '0.5px' }}>New Expiry Date</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '11px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Granted On</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {viewData.gracePeriodLogs.map((gLog, gIdx) => (
                                                        <TableRow key={gIdx} sx={{ '&:hover': { bgcolor: '#fffdf5' } }}>
                                                            <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>#{gIdx + 1}</TableCell>
                                                            <TableCell sx={{ fontSize: '12px' }}>
                                                                <Chip label={`+${gLog.graceDays} Days`} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 800, fontSize: '10px', height: '20px' }} />
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '12px', color: '#64748b' }}>{formatDate(gLog.previousExpiryDate)}</TableCell>
                                                            <TableCell sx={{ fontSize: '12px', fontWeight: 700, color: '#166534' }}>{formatDate(gLog.newExpiryDate)}</TableCell>
                                                            <TableCell sx={{ fontSize: '12px', color: '#475569' }}>{formatDate(gLog.date)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </div>
                        )}
                    </Box>
                </Modal>

                {/* Subscription History Modal */}
                <Modal open={isStoreHistoryModalOpen} onClose={() => setIsStoreHistoryModalOpen(false)}>
                    <Box className="package-modal-box" sx={{ maxWidth: '1050px !important', width: '94vw', borderRadius: '16px', overflow: 'hidden' }}>
                        <div className="modal-header-banner" style={{ background: 'linear-gradient(135deg, #3C0008 0%, #5c000c 100%)', padding: '18px 24px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon icon="mdi:history" width="22" height="22" color="#ffffff" />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, fontSize: '18px', lineHeight: 1.2 }}>
                                        Subscription History
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                                        {selectedStoreForHistory?.storeName}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton size="small" onClick={() => setIsStoreHistoryModalOpen(false)} sx={{ color: '#ffffff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>

                        <div className="modal-body-scrollable" style={{ padding: '24px', backgroundColor: '#ffffff' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                    Chronological record of store package subscriptions (newest to oldest)
                                </Typography>
                                {storeHistoryList.length > 0 && (
                                    <Chip
                                        label={`${storeHistoryList.length} ${storeHistoryList.length === 1 ? 'Subscription' : 'Subscriptions'}`}
                                        size="small"
                                        sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700, fontSize: '11.5px' }}
                                    />
                                )}
                            </Box>

                            {storeHistoryLoading ? (
                                <Box sx={{ py: 8, textAlign: 'center', color: '#64748b' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Loading subscription history...</Typography>
                                </Box>
                            ) : storeHistoryList.length === 0 ? (
                                <Box sx={{ py: 8, textAlign: 'center', color: '#94a3b8' }}>
                                    <Icon icon="mdi:package-variant-remove" width="48" height="48" style={{ opacity: 0.5, marginBottom: '8px' }} />
                                    <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600, fontSize: '15px' }}>
                                        No Subscription History Records Found
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ background: '#3C0008 !important' }}>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>TAG</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>PACKAGE NAME</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>START DATE</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>END DATE</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', color: '#ffffff !important', background: '#3C0008 !important', textTransform: 'uppercase', py: 1.5, letterSpacing: '0.6px' }}>STATUS</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {storeHistoryList.map((histSub, idx) => {
                                                const isCurrent = idx === 0 || histSub.isLatest;
                                                return (
                                                    <TableRow
                                                        key={histSub._id || idx}
                                                        sx={{
                                                            bgcolor: isCurrent ? '#f0fdf4' : 'inherit',
                                                            borderLeft: isCurrent ? '4px solid #16a34a' : '4px solid transparent',
                                                            transition: 'background-color 0.15s ease',
                                                            '&:hover': { bgcolor: isCurrent ? '#ecfdf5' : '#f8fafc' }
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 1.8 }}>
                                                            {isCurrent ? (
                                                                <Chip
                                                                    icon={<Icon icon="mdi:check-circle" width="12" height="12" style={{ color: '#15803d' }} />}
                                                                    label="CURRENT"
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#dcfce7',
                                                                        color: '#15803d',
                                                                        fontWeight: 800,
                                                                        fontSize: '10px',
                                                                        height: '22px',
                                                                        border: '1px solid #bbf7d0'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label="PREVIOUS"
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#f1f5f9',
                                                                        color: '#64748b',
                                                                        fontWeight: 700,
                                                                        fontSize: '10px',
                                                                        height: '22px'
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 1.8 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                                {histSub.packageName}
                                                            </Typography>
                                                        </TableCell>

                                                        {/* Start Date */}
                                                        <TableCell sx={{ py: 1.8 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                                <Icon icon="mdi:calendar-start-outline" width="16" height="16" color="#2563eb" />
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                                    {formatDate(histSub.validityStartDate || histSub.subscriptionStartDate)}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>

                                                        {/* End Date */}
                                                        <TableCell sx={{ py: 1.8 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                                <Icon icon="mdi:calendar-end-outline" width="16" height="16" color={histSub.subscriptionStatus === 'Expired' ? '#dc2626' : '#166534'} />
                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: histSub.subscriptionStatus === 'Expired' ? '#dc2626' : '#0f172a' }}>
                                                                    {formatDate(histSub.finalExpiryDate)}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>

                                                        {/* Status */}
                                                        <TableCell sx={{ py: 1.8 }}>
                                                            {getStatusChip(histSub.subscriptionStatus)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </div>
                    </Box>
                </Modal>

                {/* Grace Modal */}
                <Modal open={isGraceModalOpen} onClose={() => setIsGraceModalOpen(false)}>
                    <Box className="package-modal-box" sx={{ maxWidth: '400px !important' }}>
                        <div className="modal-header-banner">
                            <h3>
                                <Icon icon="mdi:calendar-clock" width="22" height="22" />
                                Extend Grace Period
                            </h3>
                            <IconButton size="small" onClick={() => setIsGraceModalOpen(false)} sx={{ color: '#ffffff' }}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </div>
                        <div className="modal-body-scrollable" style={{ padding: '24px' }}>
                            <Typography variant="body2" mb={3} color="textSecondary">
                                Enter the number of days to extend the validity of this expired package.
                            </Typography>
                            <TextField
                                fullWidth
                                label="Grace Period (Days)"
                                type="number"
                                size="small"
                                value={graceDays}
                                onChange={(e) => setGraceDays(parseInt(e.target.value) || 0)}
                                inputProps={{ min: 1 }}
                                sx={{ mb: 3 }}
                            />
                            <Box display="flex" justifyContent="flex-end" gap={2}>
                                <Button variant="outlined" onClick={() => setIsGraceModalOpen(false)} disabled={extending}>
                                    Cancel
                                </Button>
                                <Button variant="contained" color="primary" onClick={handleExtendGracePeriod} disabled={extending} sx={{ bgcolor: '#3C0008', '&:hover': { bgcolor: '#2a0005' } }}>
                                    {extending ? "Extending..." : "Confirm Extension"}
                                </Button>
                            </Box>
                        </div>
                    </Box>
                </Modal>

                {/* Package Invoice Modal */}
                <PackageInvoiceModal
                    isOpen={isInvoiceModalOpen}
                    onClose={() => setIsInvoiceModalOpen(false)}
                    subscriptionId={selectedInvoiceSubId}
                />
            </div>
        </div>
    );
};

export default SubscriptionHistoryPage;
